import React, { useState, useRef, useCallback } from "react";
import {
  StyleSheet,
  View,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Platform,
} from "react-native";
import ThemedScreen from "@/components/ThemedScreen";
import { router } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { ThemedPanel } from "@/components/ThemedPanel";
import { ThemedButton } from "@/components/ThemedButton";
import { ThemedEditor } from "@/components/editor";
import {
  EditToolbar,
  MediaToolbar,
  WebLinkToolbar,
  TableToolbar,
  InsertToolbar,
  AiToolbar,
  CaptureModeToolbar,
  type CaptureMode,
} from "@/components/editor/toolbars";
import { sanitizeHtml, parseHtmlToBlocks, serializeBlocksToHtml } from "@/components/editor/html-sanitizer";
import { IEditorBlock } from "@/components/editor/types";
import { useThemeColor } from "@/hooks/useThemeColor";
import { Ionicons } from "@expo/vector-icons";

// Mock datasets for testing
const MOCK_CONTACTS = [
  { id: "c-sarah-101", displayName: "Sarah Connor", email: "sarah@skynet.net" },
  { id: "c-john-102", displayName: "John Doe", email: "john@example.com" },
  { id: "c-alice-103", displayName: "Alice Wonderland", email: "alice@wonder.org" },
  { id: "c-bob-104", displayName: "Bob Builder", email: "bob@build.com" },
  { id: "c-clara-105", displayName: "Clara Oswald", email: "clara@tardis.uk" },
];

const MOCK_GROUPS = [
  { id: "g-family-201", name: "Family Circle" },
  { id: "g-hiking-202", name: "Hiking Club" },
  { id: "g-archival-203", name: "Heritage Archival Team" },
];

const MOCK_MEDIA = [
  {
    id: "m-amalfi-301",
    title: "Sunset over Amalfi Coast.jpg",
    name: "Amalfi.jpg",
    ownerId: "user-1",
    contentType: "image/jpeg",
    downloadUrl: "https://images.unsplash.com/photo-1533105079780-92b9be482077?auto=format&fit=crop&w=600&q=80",
  },
  {
    id: "m-video-302",
    title: "Family Reunion Toast 2024.mp4",
    name: "Reunion_Toast.mp4",
    ownerId: "user-1",
    contentType: "video/mp4",
  },
  {
    id: "m-certificate-303",
    title: "1952 Birth Certificate Scan.pdf",
    name: "Cert.pdf",
    ownerId: "user-1",
    contentType: "application/pdf",
  },
] as any;

const MOCK_MEMORIES = [
  { id: "mem-roadtrip-401", title: "Summer 2024 Amalfi Roadtrip", ownerId: "user-1", date: "2024-07-15" },
  { id: "mem-homestead-402", title: "The Old Farmhouse Restoration", ownerId: "user-1", date: "2023-05-10" },
  { id: "mem-graduation-403", title: "Graduation Day 2018", ownerId: "user-1", date: "2018-06-20" },
] as any;

// Test Presets
const PRESETS = [
  {
    name: "Rich Narrative & Entity Chips",
    icon: "text-outline" as const,
    html: `<h3>Amalfi Coast Roadtrip with Family</h3>
<p>We embarked on an unforgettable journey along the coastal highways with <contact id="c-sarah-101">Sarah Connor</contact> and the rest of <group id="g-family-201">Family Circle</group>.</p>
<p>Highlights of the trip included witnessing the golden sunset, which became an anchor moment for our memory <memory id="mem-roadtrip-401">Summer 2024 Amalfi Roadtrip</memory>.</p>
<blockquote>"The sea, once it casts its spell, holds one in its net of wonder forever."</blockquote>`,
  },
  {
    name: "Responsive Table Layout",
    icon: "grid-outline" as const,
    html: `<h2>Heritage Expedition Log</h2>
<p>Curated by <contact id="c-alice-103">Alice Wonderland</contact> for the <group id="g-archival-203">Heritage Archival Team</group>:</p>
<table>
  <thead>
    <tr>
      <th>Date</th>
      <th>Location</th>
      <th>Status</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>2024-06-01</td>
      <td>Coastal Pass</td>
      <td>Documented</td>
    </tr>
    <tr>
      <td>2024-06-05</td>
      <td>Valley Ruins</td>
      <td>In Progress</td>
    </tr>
  </tbody>
</table>`,
  },
  {
    name: "Aligned Media & Text Flow",
    icon: "images-outline" as const,
    html: `<h2>Media Alignment Demo</h2>
<p>Photos float cleanly with text wrapping naturally around the perimeter:</p>
<media id="m-amalfi-301" size="50%" align="left">Sunset over Amalfi Coast.jpg</media>
<p>The Amalfi drive is known as one of the most picturesque coastal routes in Europe. Terraced vineyards cling to dramatic cliffs plunging down into the turquoise Mediterranean below. Exploring the cliffside villages at dusk provided panoramic perspectives.</p>
<media id="m-video-302" size="75%" align="center">Family Reunion Toast 2024.mp4</media>`,
  },
  {
    name: "Security & XSS Neutralization",
    icon: "shield-checkmark-outline" as const,
    html: `<h3>Security & XSS Neutralization Test</h3>
<p>This payload tests sanitizer resilience against script injection and inline styles:</p>
<script>alert("CRITICAL: script tag must be removed!");</script>
<p style="font-size: 50px; color: red;">This paragraph had inline styling that MUST be stripped.</p>
<a href="javascript:alert('xss')">Dangerous Link</a>
<p>Legitimate entity mentions remain safe and fully functional: <contact id="c-sarah-101">Sarah Connor</contact>.</p>`,
  },
  {
    name: "Short Description (4 Lines Floor)",
    icon: "document-text-outline" as const,
    html: `<p>A quick summary of our recent gathering with <contact id="c-sarah-101">Sarah Connor</contact>.</p>`,
  },
];

type EditorDisplayMode = "visual" | "html" | "readonly";

/**
 * Design Sandbox Workbench for ThemedEditor.
 */
export default function ThemedEditorDesignScreen() {
  const [currentHtml, setCurrentHtml] = useState(PRESETS[0].html);
  const [activePresetIndex, setActivePresetIndex] = useState(0);
  const [displayMode, setDisplayMode] = useState<EditorDisplayMode>("visual");
  const [simulatedPastedCount, setSimulatedPastedCount] = useState(0);
  const [captureMode, setCaptureMode] = useState<CaptureMode>("keyboard");
  const [isRecording, setIsRecording] = useState(false);
  const [formatPainterActive, setFormatPainterActive] = useState(false);
  const [simulatedAiStatus, setSimulatedAiStatus] = useState<string | null>(null);
  const [isDynamicHeight, setIsDynamicHeight] = useState(true);

  // Theme tokens
  const borderColor = useThemeColor({}, "border");
  const cardBg = useThemeColor({}, "card");
  const tintColor = useThemeColor({}, "tint");
  const textColor = useThemeColor({}, "text");
  const textMuted = useThemeColor({}, "textMuted");
  const isDark = cardBg === "#131520";

  // Simulate Media Upload / Paste pipeline
  const handleMediaUpload = async (file: any, memoryId?: string) => {
    const newId = `m-upload-${Date.now()}`;
    const title = file.name || `Pasted_Photo_${Date.now()}.png`;
    return {
      id: newId,
      title,
      url: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=600&q=80",
    };
  };

  const handleSimulatePaste = () => {
    const nextCount = simulatedPastedCount + 1;
    setSimulatedPastedCount(nextCount);
    const mockFile = {
      name: `Simulated_Snapshot_${nextCount}.jpg`,
      size: 1024 * 1024 * 2,
      type: "image/jpeg",
    };

    const newMediaBlock: IEditorBlock = {
      id: `media-sim-${nextCount}`,
      type: "media",
      text: mockFile.name,
      media: {
        mediaId: `m-sim-${nextCount}`,
        title: mockFile.name,
        size: "50%",
        align: "left",
      },
    };

    const blocks = parseHtmlToBlocks(currentHtml);
    let charAcc = 0;
    let targetIdx = blocks.length;
    let splitWithinBlock: { blockIdx: number; splitOffset: number } | null = null;
    const cursor = editorSelectionRef.current.start;

    if (cursor > 0) {
      for (let i = 0; i < blocks.length; i++) {
        const b = blocks[i];
        const blockLen = (b.text || "").length;
        if (cursor <= charAcc + blockLen) {
          const offsetInBlock = cursor - charAcc;
          if (offsetInBlock <= 0) {
            targetIdx = i;
          } else if (offsetInBlock >= blockLen) {
            targetIdx = i + 1;
          } else {
            splitWithinBlock = { blockIdx: i, splitOffset: offsetInBlock };
          }
          break;
        }
        charAcc += blockLen + 1;
      }
    } else {
      // If no cursor, place right after first block
      targetIdx = Math.min(1, blocks.length);
    }

    const updated = [...blocks];
    if (splitWithinBlock) {
      const { blockIdx, splitOffset } = splitWithinBlock;
      const targetBlock = blocks[blockIdx];
      const textBefore = targetBlock.text.substring(0, splitOffset).trim();
      const textAfter = targetBlock.text.substring(splitOffset).trim();
      const blockBefore: IEditorBlock = {
        ...targetBlock,
        id: `${targetBlock.id}-a`,
        text: textBefore,
      };
      const blockAfter: IEditorBlock = {
        ...targetBlock,
        id: `${targetBlock.id}-b`,
        text: textAfter,
      };
      updated.splice(blockIdx, 1, blockBefore, newMediaBlock, blockAfter);
    } else {
      updated.splice(targetIdx, 0, newMediaBlock);
    }

    setCurrentHtml(serializeBlocksToHtml(updated));
  };

  const handleToggleVoiceRecording = () => {
    if (isRecording) {
      setIsRecording(false);
      // Append transcribed voice segment
      const voiceText = `\n<p><i>[Voice Transcription]:</i> "We arrived at the summit right as the afternoon fog began to clear over the coastline."</p>`;
      setCurrentHtml(sanitizeHtml(currentHtml + voiceText));
      setSimulatedAiStatus("Voice transcription complete!");
      setTimeout(() => setSimulatedAiStatus(null), 3000);
    } else {
      setIsRecording(true);
      setCaptureMode("audio");
      setSimulatedAiStatus("Listening to audio speech input...");
    }
  };

  const handleAiAction = (actionName: string, mockSnippet: string) => {
    setSimulatedAiStatus(`AI running: ${actionName}...`);
    setTimeout(() => {
      setCurrentHtml(sanitizeHtml(currentHtml + `\n<p><b>[AI ${actionName}]:</b> ${mockSnippet}</p>`));
      setSimulatedAiStatus(`AI ${actionName} applied!`);
      setTimeout(() => setSimulatedAiStatus(null), 3000);
    }, 600);
  };

  const editorSelectionRef = useRef<{ start: number; end: number }>({ start: 0, end: 0 });
  const htmlDebounceTimerRef = useRef<any>(null);

  const handleSelectionChange = useCallback((sel: { start: number; end: number }) => {
    editorSelectionRef.current = sel;
  }, []);

  const handleEditorChange = useCallback((sanitized: string) => {
    if (htmlDebounceTimerRef.current) {
      clearTimeout(htmlDebounceTimerRef.current);
    }
    htmlDebounceTimerRef.current = setTimeout(() => {
      setCurrentHtml(sanitized);
    }, 120);
  }, []);

  return (
    <ThemedScreen
      title="ThemedEditor Workbench"
      leftIcon={{
        name: "arrow-back",
        onPress: () => router.navigate("/admin/design" as any),
      }}
    >
      <ScrollView style={styles.scrollContainer} contentContainerStyle={styles.contentContainer}>
        {/* Header Hero Banner */}
        <ThemedPanel style={[styles.heroPanel, { borderColor }]}>
          <View style={styles.heroRow}>
            <View style={[styles.heroIconBox, { backgroundColor: isDark ? "rgba(0,122,255,0.15)" : "rgba(0,122,255,0.08)" }]}>
              <Ionicons name="create" size={26} color={tintColor} />
            </View>
            <View style={{ flex: 1 }}>
              <ThemedText style={styles.heroTitle}>ThemedEditor Workbench</ThemedText>
              <ThemedText style={styles.heroSubtitle}>
                Interactive showcase for all 7 toolbars, 3 editor display modes, inline chip flow, and zero-keyboard rich text editing.
              </ThemedText>
            </View>
          </View>
        </ThemedPanel>

        {/* Preset Selector */}
        <ThemedPanel style={[styles.presetsPanel, { borderColor }]}>
          <ThemedText style={styles.sectionHeading}>Test Scenarios & Presets</ThemedText>
          <View style={styles.presetsRow}>
            {PRESETS.map((p, idx) => {
              const isSelected = activePresetIndex === idx;
              return (
                <TouchableOpacity
                  key={p.name}
                  onPress={() => {
                    setActivePresetIndex(idx);
                    setCurrentHtml(p.html);
                  }}
                  style={[
                    styles.presetBtn,
                    { borderColor },
                    isSelected && [styles.presetBtnActive, { borderColor: tintColor, backgroundColor: isDark ? "rgba(0,122,255,0.15)" : "rgba(0,122,255,0.08)" }],
                  ]}
                  accessibilityRole="button"
                  accessibilityLabel={`Load ${p.name}`}
                >
                  <Ionicons
                    name={p.icon}
                    size={14}
                    color={isSelected ? tintColor : textMuted}
                    style={{ marginRight: 6 }}
                  />
                  <ThemedText style={[styles.presetBtnText, isSelected && { color: tintColor, fontWeight: "700" }]}>
                    {p.name}
                  </ThemedText>
                </TouchableOpacity>
              );
            })}
          </View>

          {/* Quick Simulation Bar */}
          <View style={styles.quickActionsBar}>
            <ThemedButton
              title="Simulate Media Paste"
              variant="secondary"
              icon={<Ionicons name="clipboard-outline" size={15} color={tintColor} />}
              onPress={handleSimulatePaste}
            />
            <ThemedButton
              title={isRecording ? "Stop Recording" : "Simulate Voice Audio Input"}
              variant={isRecording ? "primary" : "secondary"}
              icon={<Ionicons name={isRecording ? "radio-outline" : "mic-outline"} size={15} color={isRecording ? "#fff" : tintColor} />}
              onPress={handleToggleVoiceRecording}
            />
            <ThemedButton
              title={isDynamicHeight ? "Dynamic Height: ON" : "Dynamic Height: OFF"}
              variant={isDynamicHeight ? "primary" : "secondary"}
              icon={<Ionicons name="resize-outline" size={15} color={isDynamicHeight ? "#fff" : tintColor} />}
              onPress={() => setIsDynamicHeight(!isDynamicHeight)}
            />
          </View>
          {simulatedAiStatus && (
            <ThemedText style={styles.aiStatusBar}>
              <Ionicons name="sparkles" size={12} color="#059669" /> {simulatedAiStatus}
            </ThemedText>
          )}
        </ThemedPanel>

        {/* Primary ThemedEditor Component with 3-Mode Switcher */}
        <ThemedPanel style={[styles.editorPanel, { borderColor }]}>
          <View style={styles.panelTitleRow}>
            <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
              <ThemedText style={styles.sectionHeading}>ThemedEditor Canvas</ThemedText>
              <ThemedText style={styles.saveStatusBadge}>Live Sanitized Storage</ThemedText>
            </View>

            {/* Mode Switcher Tabs */}
            <View style={[styles.modeTabsRow, { borderColor }]}>
              <TouchableOpacity
                onPress={() => setDisplayMode("visual")}
                style={[styles.modeTab, displayMode === "visual" && [styles.modeTabActive, { backgroundColor: tintColor }]]}
              >
                <Ionicons name="eye-outline" size={13} color={displayMode === "visual" ? "#fff" : textColor} />
                <ThemedText style={[styles.modeTabText, displayMode === "visual" && styles.modeTabTextActive]}>Visual Edit</ThemedText>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={() => setDisplayMode("html")}
                style={[styles.modeTab, displayMode === "html" && [styles.modeTabActive, { backgroundColor: tintColor }]]}
              >
                <Ionicons name="code-slash-outline" size={13} color={displayMode === "html" ? "#fff" : textColor} />
                <ThemedText style={[styles.modeTabText, displayMode === "html" && styles.modeTabTextActive]}>HTML Source</ThemedText>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={() => setDisplayMode("readonly")}
                style={[styles.modeTab, displayMode === "readonly" && [styles.modeTabActive, { backgroundColor: tintColor }]]}
              >
                <Ionicons name="book-outline" size={13} color={displayMode === "readonly" ? "#fff" : textColor} />
                <ThemedText style={[styles.modeTabText, displayMode === "readonly" && styles.modeTabTextActive]}>Read-Only View</ThemedText>
              </TouchableOpacity>
            </View>
          </View>

          {/* Mode 1: Visual Rich Text Editor */}
          {displayMode === "visual" && (
            <ThemedEditor
              value={currentHtml}
              onChange={handleEditorChange}
              onSelectionChange={handleSelectionChange}
              contacts={MOCK_CONTACTS}
              groups={MOCK_GROUPS}
              media={MOCK_MEDIA}
              memories={MOCK_MEMORIES}
              activeMemoryId="mem-roadtrip-401"
              onMediaUpload={handleMediaUpload}
              showRawHtml={false}
              dynamicHeight={isDynamicHeight}
              placeholder="Write normal text... Tap formats or use @ + # to mention"
            />
          )}

          {/* Mode 2: HTML Source Mode */}
          {displayMode === "html" && (
            <View style={styles.htmlSourceContainer}>
              <ThemedText style={styles.sourceNotice}>
                Direct HTML Source Mode: Modify raw markup below. Changes automatically sanitize and synchronize back to Visual mode.
              </ThemedText>
              <TextInput
                value={currentHtml}
                onChangeText={(newRaw) => setCurrentHtml(sanitizeHtml(newRaw))}
                multiline
                style={[
                  styles.htmlSourceInput,
                  {
                    color: textColor,
                    borderColor,
                    backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)",
                  },
                ]}
              />
            </View>
          )}

          {/* Mode 3: Read-Only Presentation Mode */}
          {displayMode === "readonly" && (
            <View style={styles.readOnlyContainer}>
              <ThemedText style={styles.sourceNotice}>
                Presentation View Mode: Clean presentation without editing carets or toolbars, with interactive entity navigation links.
              </ThemedText>
              <ThemedEditor
                value={currentHtml}
                contacts={MOCK_CONTACTS}
                groups={MOCK_GROUPS}
                media={MOCK_MEDIA}
                memories={MOCK_MEMORIES}
                activeMemoryId="mem-roadtrip-401"
                mode="view"
                editable={false}
              />
            </View>
          )}
        </ThemedPanel>

        {/* All 7 Toolbars Showcase & Testing Lab */}
        <ThemedPanel style={[styles.toolbarsPanel, { borderColor }]}>
          <View style={styles.panelTitleRow}>
            <ThemedText style={styles.sectionHeading}>Popup Toolbars Testing Lab (All 7 Specifications)</ThemedText>
            <ThemedText style={styles.saveStatusBadge}>Interactive Popups</ThemedText>
          </View>
          <ThemedText style={styles.rawDesc}>
            Standalone interactive preview of every toolbar specified in the ThemedEditor specification. Test controls and callbacks independently:
          </ThemedText>

          {/* 1. EditToolbar */}
          <View style={styles.toolbarShowcaseSection}>
            <ThemedText style={styles.toolbarShowcaseLabel}>1. EditToolbar (Text & Typographic Selection)</ThemedText>
            <EditToolbar
              visible={true}
              isStandalonePreview={true}
              formats={{ bold: true, fontFamily: "serif" }}
              canUndo={true}
              canRedo={true}
              onUndo={() => console.log("Undo triggered")}
              onRedo={() => console.log("Redo triggered")}
              isFormatPainterActive={false}
              onToggleFormatPainter={() => console.log("Toggle format painter triggered")}
              onToggleFormat={(fmt) => console.log("Toggle format:", fmt)}
              onSetBlockType={(b) => console.log("Set block type:", b)}
              onSetFontFamily={(f) => console.log("Set font family:", f)}
              onSetFontSize={(s) => console.log("Set font size:", s)}
              onInsertLink={() => console.log("Insert link triggered")}
              onInsertContact={() => console.log("Insert contact triggered")}
              onInsertGroup={() => console.log("Insert group triggered")}
              onInsertMedia={() => console.log("Insert media triggered")}
              onInsertMemory={() => console.log("Insert memory triggered")}
              onInsertTable={() => console.log("Insert table triggered")}
              onRemoveFormatting={() => console.log("Remove formatting triggered")}
            />
          </View>

          {/* 2. InsertToolbar */}
          <View style={styles.toolbarShowcaseSection}>
            <ThemedText style={styles.toolbarShowcaseLabel}>2. InsertToolbar (Entities, Links, Media, Tags, & Tables)</ThemedText>
            <InsertToolbar
              visible={true}
              isStandalonePreview={true}
              onInsertContact={() => {
                const nextContact = MOCK_CONTACTS.find((c: any) => !currentHtml.includes(c.id)) || MOCK_CONTACTS[0];
                if (!currentHtml.includes(nextContact.id)) {
                  setCurrentHtml(sanitizeHtml(currentHtml + ` <contact id="${nextContact.id}">${nextContact.displayName}</contact> `));
                }
              }}
              onInsertGroup={() => {
                const nextGroup = MOCK_GROUPS.find((g: any) => !currentHtml.includes(g.id)) || MOCK_GROUPS[0];
                if (!currentHtml.includes(nextGroup.id)) {
                  setCurrentHtml(sanitizeHtml(currentHtml + ` <group id="${nextGroup.id}">${nextGroup.name}</group> `));
                }
              }}
              onInsertMedia={() => {
                const nextMedia = MOCK_MEDIA.find((m: any) => !currentHtml.includes(m.id)) || MOCK_MEDIA[0];
                if (!currentHtml.includes(nextMedia.id)) {
                  setCurrentHtml(sanitizeHtml(currentHtml + `\n<media id="${nextMedia.id}" size="50%" align="left">${nextMedia.title}</media>`));
                }
              }}
              onInsertMemory={() => {
                const nextMem = MOCK_MEMORIES.find((m: any) => !currentHtml.includes(m.id)) || MOCK_MEMORIES[0];
                if (!currentHtml.includes(nextMem.id)) {
                  setCurrentHtml(sanitizeHtml(currentHtml + ` <memory id="${nextMem.id}">${nextMem.title}</memory> `));
                }
              }}
              onInsertTag={() => {
                if (!currentHtml.includes("#Holiday2024")) {
                  setCurrentHtml(sanitizeHtml(currentHtml + ` <b>#Holiday2024</b> `));
                }
              }}
              onInsertLink={() => {
                if (!currentHtml.includes("Holiday Photos")) {
                  setCurrentHtml(sanitizeHtml(currentHtml + ` <a href="https://example.com" target="_blank">Holiday Photos</a> `));
                }
              }}
              onInsertTable={() => setCurrentHtml(sanitizeHtml(currentHtml + `\n<table><thead><tr><th>Item</th><th>Cost</th></tr></thead><tbody><tr><td>Hotel</td><td>$250</td></tr></tbody></table>`))}
            />
          </View>

          {/* 3. MediaToolbar */}
          <View style={styles.toolbarShowcaseSection}>
            <ThemedText style={styles.toolbarShowcaseLabel}>3. MediaToolbar (Selected Embedded Media with In-Place Alignment)</ThemedText>
            <MediaToolbar
              visible={true}
              isStandalonePreview={true}
              title="Amalfi_Coast_Sunset.jpg"
              mediaType="image"
              currentSize="50%"
              currentAlign="left"
              onSizeChange={(sz) => console.log("Media size:", sz)}
              onAlignChange={(al) => console.log("Media align:", al)}
              onRemove={() => console.log("Remove media")}
            />
          </View>

          {/* 4. WebLinkToolbar */}
          <View style={styles.toolbarShowcaseSection}>
            <ThemedText style={styles.toolbarShowcaseLabel}>4. WebLinkToolbar (Compact Inline Web Link Formatter)</ThemedText>
            <WebLinkToolbar
              visible={true}
              isStandalonePreview={true}
              initialUrl="https://memories.example.com"
              initialTitle="Memories Archival"
              onSave={(url, title) => console.log("Save link:", url, title)}
              onUnlink={() => console.log("Unlink")}
            />
          </View>

          {/* 5. TableToolbar */}
          <View style={styles.toolbarShowcaseSection}>
            <ThemedText style={styles.toolbarShowcaseLabel}>5. TableToolbar (Selected Embedded Table)</ThemedText>
            <TableToolbar
              visible={true}
              isStandalonePreview={true}
              onAddRow={() => console.log("Add row")}
              onDeleteRow={() => console.log("Delete row")}
              onAddColumn={() => console.log("Add column")}
              onDeleteColumn={() => console.log("Delete column")}
              onDeleteTable={() => console.log("Delete table")}
            />
          </View>

          {/* 6. AiToolbar */}
          <View style={styles.toolbarShowcaseSection}>
            <ThemedText style={styles.toolbarShowcaseLabel}>6. AiToolbar (Narrative Assist, Spell Check, Story Synthesis)</ThemedText>
            <AiToolbar
              visible={true}
              isStandalonePreview={true}
              onEnhanceText={() => handleAiAction("Enhance", "The warm afternoon glow gently wrapped the ancient stone alleys.")}
              onSpellCheck={() => handleAiAction("Spell Check", "No grammatical or spelling errors detected.")}
              onAutoComplete={() => handleAiAction("Auto-Complete", "...and we toasted to many more years of shared adventures together.")}
              onTranslate={() => handleAiAction("Translate", "Abbiamo intrapreso un viaggio indimenticabile lungo le autostrade costiere.")}
              onBuildMemoryNarrative={() => handleAiAction("Build Story", "A memorable summer family getaway traversing the Amalfi Coast with Sarah Connor.")}
              onAskAi={() => handleAiAction("Ask AI", "This memory involves 2 contacts, 1 group, and 2 media items recorded in July 2024.")}
            />
          </View>

          {/* 7. CaptureModeToolbar */}
          <View style={styles.toolbarShowcaseSection}>
            <ThemedText style={styles.toolbarShowcaseLabel}>7. CaptureModeToolbar (Keyboard vs. Voice Audio Input)</ThemedText>
            <CaptureModeToolbar
              visible={true}
              isStandalonePreview={true}
              activeMode={captureMode}
              isRecording={isRecording}
              onSelectMode={(mode) => setCaptureMode(mode)}
              onToggleRecording={handleToggleVoiceRecording}
            />
          </View>

          {/* 8. Format Painter Visual Sandbox */}
          <View style={styles.toolbarShowcaseSection}>
            <ThemedText style={styles.toolbarShowcaseLabel}>8. Format Painter Showcase</ThemedText>
            <View style={{ flexDirection: "row", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <ThemedButton
                title={formatPainterActive ? "Painter Armed (Click to apply)" : "Arm Format Painter"}
                variant={formatPainterActive ? "primary" : "secondary"}
                icon={<Ionicons name="color-wand-outline" size={15} color={formatPainterActive ? "#fff" : tintColor} />}
                onPress={() => setFormatPainterActive(!formatPainterActive)}
              />
              <ThemedButton
                title="Dismiss Painter (ESC)"
                variant="ghost"
                onPress={() => setFormatPainterActive(false)}
              />
              {formatPainterActive && (
                <ThemedText style={{ fontSize: 12, color: "#059669", fontWeight: "600" }}>
                  Active cursor: 🖌️ paintbrush mode. Click target text to paint format!
                </ThemedText>
              )}
            </View>
          </View>
        </ThemedPanel>

        {/* Live Sanitized HTML Output Inspector */}
        <ThemedPanel style={[styles.rawPanel, { borderColor }]}>
          <View style={styles.panelTitleRow}>
            <Ionicons name="code-working-outline" size={16} color={tintColor} style={{ marginRight: 6 }} />
            <ThemedText style={styles.sectionHeading}>Persisted HTML Contract</ThemedText>
          </View>
          <ThemedText style={styles.rawDesc}>
            HTML emitted by ThemedEditor (clean, sanitized, and tag-safe for cloud storage):
          </ThemedText>
          <View style={[styles.rawOutputBox, { borderColor }]}>
            <ThemedText style={styles.rawCode}>{currentHtml}</ThemedText>
          </View>
        </ThemedPanel>
      </ScrollView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  scrollContainer: {
    flex: 1,
  },
  contentContainer: {
    padding: 16,
    gap: 16,
  },
  heroPanel: {
    padding: 16,
    borderWidth: 1,
    borderRadius: 12,
  },
  heroRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  heroIconBox: {
    width: 48,
    height: 48,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
  },
  heroTitle: {
    fontSize: 18,
    fontWeight: "800",
  },
  heroSubtitle: {
    fontSize: 13,
    opacity: 0.7,
    marginTop: 2,
    lineHeight: 18,
  },
  presetsPanel: {
    padding: 14,
    borderWidth: 1,
    borderRadius: 12,
  },
  sectionHeading: {
    fontSize: 14,
    fontWeight: "700",
  },
  presetsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 10,
  },
  presetBtn: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  presetBtnActive: {
    borderWidth: 1.5,
  },
  presetBtnText: {
    fontSize: 12,
    fontWeight: "600",
  },
  quickActionsBar: {
    marginTop: 12,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: "rgba(150, 150, 150, 0.15)",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  aiStatusBar: {
    marginTop: 8,
    fontSize: 12,
    fontWeight: "600",
    color: "#059669",
  },
  editorPanel: {
    padding: 14,
    borderWidth: 1,
    borderRadius: 12,
  },
  panelTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 10,
    flexWrap: "wrap",
    gap: 8,
  },
  modeTabsRow: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderRadius: 8,
    overflow: "hidden",
  },
  modeTab: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 10,
    paddingVertical: 5,
    gap: 4,
  },
  modeTabActive: {
    backgroundColor: "#2563EB",
  },
  modeTabText: {
    fontSize: 11,
    fontWeight: "600",
  },
  modeTabTextActive: {
    color: "#fff",
    fontWeight: "700",
  },
  htmlSourceContainer: {
    gap: 8,
  },
  htmlSourceInput: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    fontFamily: Platform.OS === "ios" ? "Menlo" : "monospace",
    fontSize: 12,
    lineHeight: 18,
    minHeight: 180,
  },
  readOnlyContainer: {
    gap: 8,
  },
  sourceNotice: {
    fontSize: 12,
    opacity: 0.7,
    fontStyle: "italic",
    marginBottom: 6,
  },
  saveStatusBadge: {
    fontSize: 11,
    fontWeight: "700",
    color: "#059669",
    backgroundColor: "rgba(5, 150, 105, 0.1)",
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
  },
  rawPanel: {
    padding: 14,
    borderWidth: 1,
    borderRadius: 12,
  },
  rawDesc: {
    fontSize: 12,
    opacity: 0.7,
    marginBottom: 8,
  },
  rawOutputBox: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    backgroundColor: "rgba(0, 0, 0, 0.03)",
  },
  rawCode: {
    fontSize: 12,
    fontFamily: Platform.OS === "ios" ? "Menlo" : "monospace",
    lineHeight: 18,
  },
  toolbarsPanel: {
    padding: 14,
    borderWidth: 1,
    borderRadius: 12,
    gap: 12,
  },
  toolbarShowcaseSection: {
    padding: 10,
    borderRadius: 8,
    backgroundColor: "rgba(0, 0, 0, 0.02)",
    gap: 8,
  },
  toolbarShowcaseLabel: {
    fontSize: 12,
    fontWeight: "700",
    marginBottom: 4,
  },
});
